/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Buttons } from '../controls/buttons';
import { Component, OnInit, NgZone, ElementRef, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { AuthenticationService } from '../shared/authentication/authentication.service';
import { LastUrlService, LoginUrl } from '../shared/authentication/last-url.service';
import { LoginObserverService } from '../shared/authentication/login-observer.service';
import { CurrentUserService } from '../user/services/current-user.service';
import { UserService } from '../user/services/user.service';
import { AzureADSettings } from '../azure-ad/models/azure-ad-settings';

import { FormsModule } from '@angular/forms';
import { AutoFocusDirective } from '../shared/auto-focus/auto-focus.directive';
import { Textbox } from '../controls/textbox';
import { ApplicationInsightsService } from '../shared/insights/application-insights.service';

@Component({
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, AutoFocusDirective, Buttons, Textbox]
})
export class LoginComponent implements OnInit {
    private appInsightsService = inject(ApplicationInsightsService);
    private authenticationService = inject(AuthenticationService);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private lastUrlService = inject(LastUrlService);
    private userService = inject(UserService);
    private ngZone = inject(NgZone);
    private loginObserverService = inject(LoginObserverService);
    private currentUserService = inject(CurrentUserService);
    private route = inject(ActivatedRoute);

    @ViewChild('resetDialog') resetDialog: ElementRef<HTMLDialogElement>;
    @ViewChild('confirmDialog') confirmDialog: ElementRef<HTMLDialogElement>;

    username: string;
    password: string;
    rememberMe = false;
    totp: string;
    isAuthenticating: boolean;
    isRequestingMFA: boolean;
    versionString: string;
    isInitializing: boolean;
    isCheckingRefreshToken: boolean;
    todaysDate: Date = new Date();
    azureADSettings = new AzureADSettings();
    isUnknownTenant = false;
    resetUsername = '';
    isResettingPassword = false;
    maskedEmail = '';

    ngOnInit() {
        void this.initialize();
    }

    async initialize(): Promise<void> {
        this.azureADSettings = await this.authenticationService.getAzureADSettings();

        const fragment = this.route.snapshot.fragment;
        let authCode = 'undefined';

        if (fragment) {
            const params = new URLSearchParams(fragment);
            authCode = params.get('code');
            const state = params.get('state');

            if (state !== 'undefined') {
                const loginUrl = JSON.parse(decodeURI(state)) as LoginUrl;
                this.lastUrlService.setLastUrl(loginUrl);
            }

            //Replace current state. (So we cant get back to LoginPage with the back command.)
            setTimeout(() => {
                void this.router.navigate(['login'], { replaceUrl: true });
            });
        }

        if (authCode !== 'undefined') {
            await this.loginWithAuthCode(authCode);
        }
        else {
            this.isRequestingMFA = false;

            // Check if the user is logged in - if not, try to get a new access token
            this.authenticationService.isLoggedIn().then(isLoggedIn => {
                console.log('Is logged in: ' + isLoggedIn);
                if (!isLoggedIn) {
                    this.isCheckingRefreshToken = true;
                    console.log('Try to get new access token...');
                    void this.authenticationService.tryGetNewAccessToken().then(success => {
                        console.log('Got new access token: ' + success);
                        if (success) {
                            this.userService.getCurrentUser(true, false).then(user => { // Make sure the user is loaded before navigating away from the login page
                                this.isCheckingRefreshToken = false;
                                if (user) {
                                    this.currentUserService.setCurrentUser(user);
                                    console.log('User logged in after getting new access token...');
                                    this.loginObserverService.userLogIn(); // Notify subscribers that we are logged in
                                    this.navigateToLastUrl();
                                }
                            }).catch(error => {
                                console.error(error);
                                this.isCheckingRefreshToken = false;
                            });
                        }
                        else {
                            this.isCheckingRefreshToken = false;
                        }
                    });
                }
                else {
                    this.navigateToLastUrl();
                }
            }).catch(() => { this.isCheckingRefreshToken = false; });
        }
    }

    async login() {
        try {
            this.isAuthenticating = true;

            await this.authenticationService.login(this.username, this.password, null, this.rememberMe);

            this.navigateToLastUrl();
        }
        catch (error) {
            if (error.status === 401) {
                const message = error.error.ReasonPhrase as string;
                this.toastService.showToast(message, ToastType.error, 2000);
            }
            else if (error.status === 403) {
                this.toastService.showToast('Account is disabled', ToastType.error, 2000);
                this.password = null;
            }
            else if (error.status === 469) { // Authentication code is required
                this.isRequestingMFA = true;
            }
            else {
                this.toastService.showToast('Wrong username or password', ToastType.error, 2000);
                this.password = null;
            }
        }
        finally {
            this.isAuthenticating = false;
        }
    }

    async loginWithAuthCode(authCode: string) {
        try {
            this.isAuthenticating = true;

            const redirectUri = location.origin + '/login';
            await this.authenticationService.loginWithAuthCode(authCode, true, redirectUri);

            this.navigateToLastUrl();
        }
        catch (error) {
            if (error.status === 401) {
                const message = error.error.ReasonPhrase as string;
                this.toastService.showToast(message, ToastType.error, 5000);
            }
            if (error.status === 491) {
                this.toastService.showToast('Azure AD user does not have sufficient permissions to log into Link', ToastType.error, 5000);
            }
            if (error.status === 492) {
                this.isUnknownTenant = true;
            }
            if (error.status === 423) {
                this.toastService.showToast('The user is disabled', ToastType.error, 5000);
            }
        }
        finally {
            this.isAuthenticating = false;
        }
    }

    async verify() {
        try {
            await this.authenticationService.login(this.username, this.password, this.totp, this.rememberMe);
            this.navigateToLastUrl();
            this.password = null;
            this.isRequestingMFA = false;
        }
        catch {
            this.toastService.showToast('Two-factor authentication failed.', ToastType.error, 2000);
        }
        finally {
            this.totp = null;
        }
    }

    onResetPassword(event: Event) {
        event.preventDefault();

        this.appInsightsService.logEvent('PasswordReset', { state: 'Start' });
        this.openResetDialog();
    }

    onResetCancel(event?: Event) {
        if (event) {
            event.preventDefault();
        }
        this.closeResetDialog();
    }

    async onResetConfirm() {
        const username = (this.resetUsername || '').trim();
        if (!username || this.isResettingPassword) {
            return;
        }

        this.isResettingPassword = true;
        try {
            await this.authenticationService.requestPasswordReset(username);
            this.closeResetDialog();
            this.openConfirmDialog(username);
        }
        catch {
            this.toastService.showToast('Failed to request password reset.', ToastType.error);
        }
        finally {
            this.isResettingPassword = false;
        }
    }

    onConfirmClose(event?: Event) {
        if (event) {
            event.preventDefault();
        }
        this.closeConfirmDialog();
    }

    onDidNotGetEmail(event?: Event) {
        if (event) {
            event.preventDefault();
        }
        this.closeConfirmDialog();
        this.openResetDialog();
    }

    private openResetDialog() {
        this.resetUsername = (this.username || '').trim();
        const dialog = this.resetDialog?.nativeElement;
        if (dialog && !dialog.open) {
            dialog.showModal();
        }
    }

    private closeResetDialog() {
        const dialog = this.resetDialog?.nativeElement;
        if (dialog?.open) {
            dialog.close();
        }
        this.resetUsername = '';
    }

    private openConfirmDialog(username: string) {
        this.maskedEmail = this.maskEmail(username);
        const dialog = this.confirmDialog?.nativeElement;
        if (dialog && !dialog.open) {
            dialog.showModal();
        }
    }

    private closeConfirmDialog() {
        const dialog = this.confirmDialog?.nativeElement;
        if (dialog?.open) {
            dialog.close();
        }
        this.maskedEmail = '';
    }

    private maskEmail(email: string): string {
        if (!email || email.indexOf('@') === -1) {
            return '***@***';
        }
        const parts = email.split('@');
        const username = parts[0];
        const domain = parts[1];

        const maskedUsername = username.length > 2
            ? username.substring(0, 2) + '*'.repeat(Math.min(username.length - 2, 3))
            : '***';

        const domainParts = domain.split('.');
        const maskedDomain = domainParts.length > 1
            ? domainParts[0].substring(0, 3) + '*'.repeat(Math.min(domainParts[0].length - 3, 6)) + '.' + domainParts[domainParts.length - 1]
            : '***';

        return maskedUsername + '@' + maskedDomain;
    }

    public mfaBack() {
        this.totp = null;
        this.password = null;
        this.isRequestingMFA = false;
    }

    private navigateToLastUrl() {
        const loginUrl = this.lastUrlService.getLastUrl();
        let lastUrl = '/';
        let parms = null;
        if (loginUrl && lastUrl.indexOf('restricted-access') < 0) { // If the previous page was a restricted-access page, just navigate to the frontpage.
            lastUrl = loginUrl.path || '/';
            parms = loginUrl.parms || null;
        }

        lastUrl = lastUrl.startsWith('/') ? lastUrl : '/' + lastUrl; // Make sure we navigate from the root

        parms = Object.assign({}, parms); // To prevent error: object is not extensible - clone the parameters

        if (parms) {
            parms.forceRedirect = new Date().getMilliseconds().toString();
        }

        this.lastUrlService.clearLastUrl();
        this.isInitializing = true;

        if (parms && lastUrl !== '/') { // Check for root-path to prevent this error: "Error: Root segment cannot have matrix parameters"
            this.ngZone.run(() => {
                this.router.navigate([lastUrl, parms]).then(result => {
                    if (!result) {
                        this.toastService.showToast('Failed to navigate, hit F5 to continue...', ToastType.error, 5000);
                    }
                    console.log(`Redirect from login to ${lastUrl} ${result ? 'succeeded' : 'failed'}.`);
                }).catch(error => {
                    console.log(`An error occurred while redirecting from login to ${lastUrl}`);
                    console.log(parms);
                    console.error(error);
                });
            });
        }
        else {
            this.ngZone.run(() => {
                this.router.navigate([lastUrl]).then(result => {
                    if (!result) {
                        this.toastService.showToast('Failed to navigate, hit F5 to continue...', ToastType.error, 5000);
                    }
                    console.log(`Redirect from login to ${lastUrl} ${result ? 'succeeded' : 'failed'}.`);
                }).catch(error => {
                    console.log(`An error occurred while redirecting from login to ${lastUrl}`);
                    console.error(error);
                });
            });
        }

        this.toastService.showToast('Successfully logged in', ToastType.success, 2000);
    }

    onUnknownTenantOk() {
        this.isUnknownTenant = false;
    }

    openAzureLogin() {
        const lastUrl = this.lastUrlService.getLastUrl();
        const state = encodeURI(JSON.stringify(lastUrl));
        this.authenticationService.navigateToAzureLogin(state);
    }
}
